import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PROJECT_STATUSES, PROJECT_CATEGORIES, LEAD_STATUSES, statusColor } from "@/data/site";
import {
  LayoutDashboard, FolderKanban, Users, Settings, LogOut, Plus, Trash2, Pencil,
  Download, Loader2, Upload, X, FileText, Search,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "projects", label: "Projects", icon: FolderKanban },
  { key: "leads", label: "Leads", icon: Users },
  { key: "content", label: "Content & SEO", icon: Settings },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-60 shrink-0 border-r border-border bg-secondary flex flex-col fixed h-screen">
        <div className="p-5 border-b border-border"><Logo markSize={36} /></div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === t.key ? "bg-primary/15 text-primary border border-primary/30" : "text-muted-foreground hover:text-primary hover:bg-white/5"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <p className="text-xs text-muted-foreground px-3 mb-2 truncate">{user?.email}</p>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-red-400 hover:bg-white/5">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 ml-60 p-8">
        {tab === "overview" && <Overview />}
        {tab === "projects" && <Projects />}
        {tab === "leads" && <Leads />}
        {tab === "content" && <Content />}
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const cards = stats ? [
    { label: "Total Leads", value: stats.total_leads },
    { label: "New Leads", value: stats.new_leads },
    { label: "Projects", value: stats.projects },
    { label: "Site Submissions", value: stats.by_type?.site_submission || 0 },
    { label: "Partner Inquiries", value: stats.by_type?.partner || 0 },
    { label: "Contractor Applications", value: stats.by_type?.contractor || 0 },
  ] : [];
  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-foreground mb-2">Dashboard</h1>
      <p className="text-muted-foreground mb-8">Overview of leads, submissions and projects.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-border bg-card p-6">
            <div className="font-display text-4xl font-extrabold text-foreground">{c.value}</div>
            <div className="text-sm text-muted-foreground mt-1">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_PROJECT = {
  name: "", location: "", acreage: "", development_type: "", category: "Data Centers",
  power_potential: "", utility_info: "", status: "Early Stage", summary: "", description: "",
  map_embed: "", images: [], documents: [], sections: [], featured: false, published: true,
};

function Projects() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/projects").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-display text-3xl font-bold text-foreground">Projects</h1><p className="text-muted-foreground">Manage the development pipeline.</p></div>
        <Button onClick={() => setEditing({ ...EMPTY_PROJECT })} data-testid="admin-add-project" className="bg-primary text-white rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Add Project</Button>
      </div>
      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4" data-testid={`admin-project-${p.slug}`}>
            {p.images?.[0] ? (
              <img src={p.images[0].startsWith("http") ? p.images[0] : fileUrl(p.images[0])} alt="" className="h-14 w-14 rounded-lg object-cover bg-secondary shrink-0" />
            ) : (
              <div className="h-14 w-14 rounded-lg bg-secondary shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-foreground truncate">{p.name}</span>
                <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${statusColor(p.status)}`}>{p.status}</span>
                {p.featured && <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-primary/30 text-primary">Featured</span>}
                {!p.published && <span className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">Draft</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{p.category} · {p.location}</div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-project-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      {editing && <ProjectEditor project={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProjectEditor({ project, onClose, onSaved }) {
  const [p, setP] = useState(project);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("is_public", "true");
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setP((prev) => ({ ...prev, images: [...(prev.images || []), data.storage_path] }));
    toast.success("Image uploaded");
  };
  const uploadDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("is_public", "true");
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setP((prev) => ({ ...prev, documents: [...(prev.documents || []), { name: f.name, path: data.storage_path, is_public: true }] }));
    toast.success("Document added");
  };

  const save = async () => {
    if (!p.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (p.id) await api.put(`/admin/projects/${p.id}`, p);
      else await api.post("/admin/projects", p);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const inputCls = "mt-1.5 bg-secondary/50 border-border";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-foreground">{p.id ? "Edit" : "New"} Project</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Name</Label><Input value={p.name} onChange={set("name")} data-testid="project-name" className={inputCls} /></div>
            <div><Label className="text-xs text-muted-foreground">Location</Label><Input value={p.location} onChange={set("location")} className={inputCls} /></div>
            <div><Label className="text-xs text-muted-foreground">Acreage</Label><Input value={p.acreage} onChange={set("acreage")} className={inputCls} /></div>
            <div><Label className="text-xs text-muted-foreground">Development Type</Label><Input value={p.development_type} onChange={set("development_type")} className={inputCls} /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={p.category} onValueChange={(v) => setP({ ...p, category: v })}><SelectTrigger className={inputCls}><SelectValue /></SelectTrigger><SelectContent>{PROJECT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={p.status} onValueChange={(v) => setP({ ...p, status: v })}><SelectTrigger className={inputCls}><SelectValue /></SelectTrigger><SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Power Potential</Label><Input value={p.power_potential} onChange={set("power_potential")} className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-muted-foreground">Utility Information</Label><Input value={p.utility_info} onChange={set("utility_info")} className={inputCls} /></div>
          <div><Label className="text-xs text-muted-foreground">Summary</Label><Textarea value={p.summary} onChange={set("summary")} rows={2} className={inputCls} /></div>
          <div><Label className="text-xs text-muted-foreground">Description</Label><Textarea value={p.description} onChange={set("description")} rows={4} className={inputCls} /></div>
          <div><Label className="text-xs text-muted-foreground">Map Embed (iframe HTML)</Label><Textarea value={p.map_embed} onChange={set("map_embed")} rows={2} className={inputCls} /></div>

          <div>
            <Label className="text-xs text-muted-foreground">Images</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(p.images || []).map((img, i) => (
                <div key={i} className="relative group">
                  <img src={img.startsWith("http") ? img : fileUrl(img)} alt="" className="h-16 w-16 rounded object-cover" />
                  <button onClick={() => setP({ ...p, images: p.images.filter((_, idx) => idx !== i) })} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"><X className="h-3 w-3 text-white" /></button>
                </div>
              ))}
              <label className="h-16 w-16 rounded border border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/40"><Upload className="h-4 w-4 text-muted-foreground" /><input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="project-image-upload" /></label>
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Documents</Label>
            <div className="space-y-2 mt-2">
              {(p.documents || []).map((d, i) => (
                <div key={i} className="flex items-center gap-3 rounded border border-border px-3 py-2">
                  <FileText className="h-4 w-4 text-primary" /><span className="text-sm text-foreground flex-1 truncate">{d.name}</span>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                    <Checkbox checked={d.is_public} onCheckedChange={(v) => { const docs = [...p.documents]; docs[i] = { ...d, is_public: !!v }; setP({ ...p, documents: docs }); }} /> Public
                  </label>
                  <button onClick={() => setP({ ...p, documents: p.documents.filter((_, idx) => idx !== i) })}><X className="h-4 w-4 text-red-400" /></button>
                </div>
              ))}
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-primary"><Upload className="h-4 w-4" /> Add document<input type="file" className="hidden" onChange={uploadDoc} data-testid="project-doc-upload" /></label>
            </div>
          </div>

          <div className="flex items-center gap-8">
            <label className="flex items-center gap-2 text-sm text-foreground"><Switch checked={p.featured} onCheckedChange={(v) => setP({ ...p, featured: v })} data-testid="project-featured" /> Featured</label>
            <label className="flex items-center gap-2 text-sm text-foreground"><Switch checked={p.published} onCheckedChange={(v) => setP({ ...p, published: v })} data-testid="project-published" /> Published</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-border">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-project" className="bg-primary text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Project"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LEAD_TYPES = [
  { key: "All", label: "All" },
  { key: "site_submission", label: "Site Submissions" },
  { key: "partner", label: "Partner Inquiries" },
  { key: "contractor", label: "Contractors" },
  { key: "contact", label: "Contact" },
];

function Leads() {
  const [list, setList] = useState([]);
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (type !== "All") params.set("lead_type", type);
    if (status !== "All") params.set("status", status);
    api.get(`/admin/leads?${params.toString()}`).then((r) => setList(r.data));
  }, [type, status]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (type !== "All") params.set("lead_type", type);
    const res = await api.get(`/admin/leads/export?${params.toString()}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "12stone-leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = list.filter((l) => !q || `${l.name} ${l.company} ${l.email} ${l.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div><h1 className="font-display text-3xl font-bold text-foreground">Leads</h1><p className="text-muted-foreground">Submissions, inquiries and applications.</p></div>
        <Button onClick={exportCsv} data-testid="admin-export-csv-btn" className="bg-primary text-white rounded-full"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="flex gap-2 flex-wrap">
          {LEAD_TYPES.map((t) => (
            <button key={t.key} onClick={() => setType(t.key)} data-testid={`lead-filter-${t.key}`} className={`text-xs px-3 py-1.5 rounded-full border ${type === t.key ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-primary"}`}>{t.label}</button>
          ))}
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40 h-9 bg-secondary/50 border-border"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All statuses</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <div className="relative flex-1 min-w-[180px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9 h-9 bg-secondary/50 border-border" data-testid="lead-search" /></div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-muted-foreground">
            <tr>{["Date", "Name", "Type", "Category", "Email", "Status", ""].map((h) => <th key={h} className="text-left font-mono text-[10px] uppercase tracking-wider px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-border hover:bg-white/5 cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{(l.submission_date || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-foreground">{l.name || "—"}<div className="text-xs text-muted-foreground">{l.company}</div></td>
                <td className="px-4 py-3 text-muted-foreground">{l.lead_type}</td>
                <td className="px-4 py-3 text-muted-foreground">{l.category || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{l.email}</td>
                <td className="px-4 py-3"><span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${statusColor(l.status)}`}>{l.status}</span></td>
                <td className="px-4 py-3 text-primary text-xs">View</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No leads found.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <LeadDetail lead={selected} onClose={() => setSelected(null)} onUpdated={() => { load(); setSelected(null); }} />}
    </div>
  );
}

function LeadDetail({ lead, onClose, onUpdated }) {
  const [l, setL] = useState(lead);
  const save = async () => {
    await api.put(`/admin/leads/${l.id}`, { status: l.status, notes: l.notes, follow_up_date: l.follow_up_date, assigned_to: l.assigned_to });
    toast.success("Lead updated");
    onUpdated();
  };
  const del = async () => { if (!window.confirm("Delete this lead?")) return; await api.delete(`/admin/leads/${l.id}`); toast.success("Deleted"); onUpdated(); };
  const payload = l.payload || {};
  const inputCls = "mt-1.5 bg-secondary/50 border-border";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-foreground">{l.name || "Lead"} · <span className="text-muted-foreground font-normal text-sm">{l.lead_type}</span></DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Email" value={l.email} /><Info label="Phone" value={l.phone} />
            <Info label="Company" value={l.company} /><Info label="Category" value={l.category} />
            <Info label="Property" value={l.property_address} /><Info label="Source" value={l.source} />
          </div>
          {l.message && <div><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Message</div><p className="text-sm text-muted-foreground mt-1">{l.message}</p></div>}

          {Object.keys(payload).length > 0 && (
            <div className="rounded-lg border border-border p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2">Submission Details</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {Object.entries(payload).filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}: </span><span className="text-foreground">{String(v)}</span></div>
                ))}
              </div>
            </div>
          )}

          {l.files?.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Attachments</div>
              <div className="space-y-2">
                {l.files.map((f, i) => (
                  <a key={i} href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline"><FileText className="h-4 w-4" /> {f.name}</a>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={l.status} onValueChange={(v) => setL({ ...l, status: v })}><SelectTrigger className={inputCls} data-testid="lead-status-select"><SelectValue /></SelectTrigger><SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Assigned To</Label><Input value={l.assigned_to || ""} onChange={(e) => setL({ ...l, assigned_to: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-xs text-muted-foreground">Follow-up Date</Label><Input type="date" value={l.follow_up_date || ""} onChange={(e) => setL({ ...l, follow_up_date: e.target.value })} className={inputCls} /></div>
          </div>
          <div><Label className="text-xs text-muted-foreground">Notes</Label><Textarea value={l.notes || ""} onChange={(e) => setL({ ...l, notes: e.target.value })} rows={3} className={inputCls} data-testid="lead-notes" /></div>
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="ghost" onClick={del} className="text-red-400"><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
          <Button onClick={save} data-testid="save-lead" className="bg-primary text-white">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  if (!value) return null;
  return <div><span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground block">{label}</span><span className="text-foreground">{value}</span></div>;
}

function Content() {
  const [contact, setContact] = useState(null);
  const [seo, setSeo] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  useEffect(() => {
    api.get("/content/contact").then((r) => setContact(r.data));
    api.get("/content/seo").then((r) => setSeo(r.data));
    api.get("/content/pipeline").then((r) => setPipeline(r.data));
  }, []);
  const saveContact = async () => { await api.put("/admin/content/contact", contact); toast.success("Contact info saved"); };
  const saveSeo = async () => { await api.put("/admin/content/seo", seo); toast.success("SEO settings saved"); };
  const setPl = (patch) => setPipeline((p) => ({ ...p, ...patch }));
  const setPlState = (i, k, v) => setPipeline((p) => ({ ...p, states: p.states.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const setPlTotals = (k, v) => setPipeline((p) => ({ ...p, totals: { ...p.totals, [k]: v } }));
  const savePipeline = async () => { await api.put("/admin/content/pipeline", pipeline); toast.success("Pipeline saved"); };
  const inputCls = "mt-1.5 bg-secondary/50 border-border";
  if (!contact || !seo || !pipeline) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl font-bold text-foreground mb-8">Content & SEO</h1>
      <div className="rounded-xl border border-border bg-card p-6 mb-6">
        <h2 className="font-display text-lg font-semibold text-foreground mb-4">Contact Information</h2>
        <div className="space-y-4">
          {[["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["address", "Headquarters / Mailing Address"], ["linkedin", "LinkedIn URL"]].map(([k, label]) => (
            <div key={k}><Label className="text-xs text-muted-foreground">{label}</Label><Input value={contact[k] || ""} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} data-testid={`contact-${k}`} className={inputCls} /></div>
          ))}
        </div>
        <Button onClick={saveContact} data-testid="save-contact" className="mt-5 bg-primary text-white">Save Contact Info</Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 mb-6">
        <h2 className="font-display text-lg font-semibold text-foreground mb-1">Project Pipeline</h2>
        <p className="text-xs text-muted-foreground mb-4">State portfolios shown on the Projects page and homepage. Power figures display as preliminary potential capacity under review.</p>
        {pipeline.states.map((s, i) => (
          <div key={i} className="mb-4 pb-4 border-b border-border">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">State</Label><Input value={s.state || ""} onChange={(e) => setPlState(i, "state", e.target.value)} className={inputCls} data-testid={`pipeline-state-${i}`} /></div>
              <div><Label className="text-xs text-muted-foreground">Potential Power</Label><Input value={s.power || ""} onChange={(e) => setPlState(i, "power", e.target.value)} className={inputCls} /></div>
              <div><Label className="text-xs text-muted-foreground">Acres</Label><Input value={s.acres || ""} onChange={(e) => setPlState(i, "acres", e.target.value)} className={inputCls} /></div>
              <div><Label className="text-xs text-muted-foreground">Parcels</Label><Input value={s.parcels || ""} onChange={(e) => setPlState(i, "parcels", e.target.value)} className={inputCls} /></div>
            </div>
          </div>
        ))}
        <Label className="text-xs text-muted-foreground">Aggregate Totals</Label>
        <div className="grid grid-cols-2 gap-3 mt-1.5">
          <Input value={pipeline.totals?.acres || ""} onChange={(e) => setPlTotals("acres", e.target.value)} placeholder="Total acres" className={inputCls} />
          <Input value={pipeline.totals?.parcels || ""} onChange={(e) => setPlTotals("parcels", e.target.value)} placeholder="Total parcels" className={inputCls} />
          <Input value={pipeline.totals?.power || ""} onChange={(e) => setPlTotals("power", e.target.value)} placeholder="Total MW" className={inputCls} />
          <Input value={pipeline.totals?.powerAlt || ""} onChange={(e) => setPlTotals("powerAlt", e.target.value)} placeholder="Total GW (alt)" className={inputCls} />
        </div>
        <div className="mt-3"><Label className="text-xs text-muted-foreground">Power Label</Label><Input value={pipeline.powerLabel || ""} onChange={(e) => setPl({ powerLabel: e.target.value })} className={inputCls} /></div>
        <div className="mt-3"><Label className="text-xs text-muted-foreground">Disclaimer</Label><Textarea value={pipeline.disclaimer || ""} onChange={(e) => setPl({ disclaimer: e.target.value })} rows={2} className={inputCls} /></div>
        <Button onClick={savePipeline} data-testid="save-pipeline" className="mt-5 bg-primary text-white">Save Pipeline</Button>
      </div>
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-lg font-semibold text-foreground mb-4">SEO Settings</h2>
        <div className="space-y-4">
          <div><Label className="text-xs text-muted-foreground">Meta Title</Label><Input value={seo.title || ""} onChange={(e) => setSeo({ ...seo, title: e.target.value })} className={inputCls} /></div>
          <div><Label className="text-xs text-muted-foreground">Meta Description</Label><Textarea value={seo.description || ""} onChange={(e) => setSeo({ ...seo, description: e.target.value })} rows={3} className={inputCls} /></div>
          <div><Label className="text-xs text-muted-foreground">Keywords</Label><Textarea value={seo.keywords || ""} onChange={(e) => setSeo({ ...seo, keywords: e.target.value })} rows={2} className={inputCls} /></div>
        </div>
        <Button onClick={saveSeo} data-testid="save-seo" className="mt-5 bg-primary text-white">Save SEO Settings</Button>
      </div>
    </div>
  );
}
